// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

// others
import { Maybe, useFetchAllUsersLazyQuery, User } from '../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT, QUERY_PARAMS } from '../../constants'
import { MatterDropdownProps } from '../../interfaceTypes/props'

const UserFilter: FC<MatterDropdownProps> = ({ label, required, disabled, setParams, getFilterValues }) => {
  const [users, setUsers] = useState<Maybe<User>[]>([])
  const [userPage, setUserPage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<Maybe<User>>(null)

  const [debouncedUserSearchTerm] = useDebounce(searchTerm, 500)

  const [fetchUsers, { loading }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    // fetchPolicy: 'no-cache',
    // nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: undefined,

    onError() {
      setUsers([])
      setHasMorePages(false)
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllUsers } = data

        if (fetchAllUsers) {
          const { users: newUsers, pagination } = fetchAllUsers
          const { page: currentPageNo, totalPages } = pagination || {}

          const newHasMore = currentPageNo && totalPages ? currentPageNo < totalPages : false

          if (newUsers) {
            setUsers(prevUsers => {
              const updatedCases = userPage === 1 ? newUsers : [...(prevUsers || []), ...newUsers]
              return updatedCases
            })

            setHasMorePages(newHasMore)
          } else {
            setHasMorePages(false)
          }
        }
      }
    }
  })

  useEffect(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.USER)
    if (!selectedId || selectedId === QUERY_PARAMS.ALL) {
      setInputValue('All')
    }
  }, [getFilterValues])

  useEffect(() => {
    if (debouncedUserSearchTerm === '' && selectedUser) return

    setUsers([])
    setUserPage(1)
    setHasMorePages(true)
  }, [debouncedUserSearchTerm, selectedUser])

  useEffect(() => {
    const searchValue = debouncedUserSearchTerm.trim()
    const keyword = searchValue || getFilterValues?.(QUERY_PARAMS.KEYWORD)

    hasMorePages &&
      fetchUsers({
        variables: {
          userInput: {
            keyword: keyword !== QUERY_PARAMS.ALL ? keyword : null,
            paginationOptions: {
              limit: PAGE_LIMIT,
              page: userPage
            }
          }
        }
      })
  }, [userPage, debouncedUserSearchTerm, fetchUsers, hasMorePages])

  const ALL_OPTION = { id: QUERY_PARAMS.ALL, title: 'All' }

  const options = useMemo(() => {
    const userOptions = users || []
    return [ALL_OPTION, ...userOptions]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  const getOptionLabel = (option: User | { id: string; title?: string }) => {
    if ('title' in option && option.title) return option.title
    const user = option as User
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
    return name || (option.id === QUERY_PARAMS.ALL ? 'All' : 'Loading...')
  }

  const value = useMemo(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.USER)
    if (selectedUser && selectedUser.id === selectedId) return selectedUser
    if (selectedId === QUERY_PARAMS.ALL || !selectedId) return ALL_OPTION

    const selected = users?.find(user => user?.id === selectedId)
    if (selected) return selected

    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, getFilterValues, selectedUser])

  useEffect(() => {
    if (searchTerm === '' && inputValue === '') {
      if (value?.id === QUERY_PARAMS.ALL) setInputValue('All')
      else if (value?.id) setInputValue(getOptionLabel(value))
    }
  }, [value, searchTerm, inputValue])

  useEffect(() => {
    const selectedId = getFilterValues?.(QUERY_PARAMS.USER)
    if (selectedId && selectedId !== QUERY_PARAMS.ALL && users.length > 0) {
      const selected = users?.find(user => user?.id === selectedId)
      if (!selected && (!selectedUser || selectedUser.id !== selectedId)) {
        const findUserInAllPages = async () => {
          let page = 1
          let found = false

          while (!found && page <= PAGE_LIMIT) {
            try {
              const result = await fetchUsers({
                variables: {
                  userInput: {
                    paginationOptions: {
                      limit: PAGE_LIMIT,
                      page: page
                    }
                  }
                }
              })

              if (result.data?.fetchAllUsers?.users) {
                const foundCase = result.data.fetchAllUsers.users.find(user => user?.id === selectedId)
                if (foundCase) {
                  setSelectedUser(foundCase)
                  found = true
                }
              }

              if (!result.data?.fetchAllUsers?.users || result.data.fetchAllUsers.users.length < PAGE_LIMIT) break // No more pages

              page++
            } catch (error) {
              console.error('Error finding selected case:', error)
              break
            }
          }
        }

        findUserInAllPages()
      }
    }
  }, [getFilterValues, fetchUsers, selectedUser])

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 && hasMorePages && !loading) {
      setUserPage(prev => prev + 1)
    }
  }

  const handleInputChange = useCallback((event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setSearchTerm(newInputValue)
      setInputValue(newInputValue)
    }
  }, [])

  const handleChange = (event: React.SyntheticEvent, newValue: Maybe<User> | null) => {
    if (newValue) {
      setParams?.(QUERY_PARAMS.USER, newValue.id || QUERY_PARAMS.ALL)
      if (searchTerm && searchTerm !== QUERY_PARAMS.ALL) {
        setParams?.(QUERY_PARAMS.KEYWORD, searchTerm)
      }

      if (newValue.id !== QUERY_PARAMS.ALL) setSelectedUser(newValue)
      else setSelectedUser(null)

      setSearchTerm('')

      if (newValue.id === QUERY_PARAMS.ALL) setInputValue('All')
      else {
        const fullName = getOptionLabel(newValue)
        setInputValue(fullName)
      }
    } else {
      setParams?.(QUERY_PARAMS.USER, QUERY_PARAMS.ALL)
      setParams?.(QUERY_PARAMS.KEYWORD, '')
      setSelectedUser(null)
      setInputValue('All')
      setSearchTerm('')
    }
  }

  return (
    <Autocomplete
      options={options}
      getOptionLabel={option => {
        if (option?.id === QUERY_PARAMS.ALL) return 'All'
        return getOptionLabel(option as User)
      }}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(event, value: User | { id: QUERY_PARAMS; title: string } | null) => {
        handleChange(event, value as Maybe<User> | null)
      }}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      disabled={disabled}
      value={value as User | { id: QUERY_PARAMS; title: string } | null}
      renderInput={params => <TextField {...params} label={label} fullWidth required={required} />}
      ListboxProps={{
        role: 'list-box',
        style: {
          maxHeight: '190px'
        },
        onScroll: handleScroll
      }}
      sx={{ width: '100%' }}
      noOptionsText={loading ? 'Loading...' : users?.length === 0 ? 'No Users' : 'No options'}
      loading={loading}
      freeSolo={false}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      disableCloseOnSelect={false}
      clearText="Clear"
      clearOnEscape
      disableClearable={false}
    />
  )
}

export default memo(UserFilter)
